import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search, MapPin, ShieldAlert, Medal, Clock, Euro, ChevronLeft, ChevronRight, Building2, RotateCcw } from 'lucide-react';
import { ProfessionalDashboardSchil } from '../components/ProfessionalDashboardSchil';
import { werkplekApi, type ComplianceTier, type MarktOpdracht } from '../services/professionalWerkplekApi';
import ReisradiusMap from '../components/ReisradiusMap';
import { PROFESSIONAL_TYPE_LABELS, type ProfessionalType } from '../types/professionalRates';

const TIER_STIJL: Record<ComplianceTier, string> = {
  GOUD: 'bg-gold-100 text-gold-800 border-gold-300',
  ZILVER: 'bg-gray-100 text-gray-700 border-gray-300',
  BRONS: 'bg-orange-50 text-orange-800 border-orange-200',
  ONGEVERIFIEERD: 'bg-navy-50 text-navy-600 border-navy-200',
  NON_COMPLIANT_HOLD: 'bg-red-50 text-red-700 border-red-200',
};

const typeLabel = (type: string) => PROFESSIONAL_TYPE_LABELS[type as ProfessionalType] ?? type;

const euro = (n: number | null) =>
  n === null || n === undefined ? null : new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(n));

const OpdrachtKaart: React.FC<{ o: MarktOpdracht }> = ({ o }) => {
  const { t } = useTranslation('professional');
  const regio = o.provincie || (o.active_regions && o.active_regions.length ? o.active_regions.join(', ') : t('werkplek.opdrachten.landelijk'));
  // Hemelsbreed tot het kantoor van de opdrachtgever, net als de reistijdvergoeding.
  const afstand =
    o.work_preference === 'remote'
      ? t('werkplek.opdrachten.opAfstand')
      : o.afstand_km !== null
        ? t('werkplek.opdrachten.afstandTotOpdrachtgever', { km: o.afstand_km })
        : null;
  const tarief =
    o.tarief_vanaf || o.tarief_tot
      ? [euro(o.tarief_vanaf), euro(o.tarief_tot)].filter(Boolean).join(' – ')
      : euro(o.hourly_rate_max);
  const uren =
    o.uren_per_week_min || o.uren_per_week_max
      ? t('werkplek.opdrachten.urenPerWeek', { uren: [o.uren_per_week_min, o.uren_per_week_max].filter(Boolean).join('–') })
      : null;
  return (
    <Link
      to={`/opdrachten/${o.id}`}
      className="block rounded-xl border border-gray-200 bg-white p-4 hover:shadow-md hover:border-gray-300 transition"
    >
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg bg-navy-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {o.organisatie_logo ? (
            <img src={o.organisatie_logo} alt="" className="w-full h-full object-contain" />
          ) : (
            <Building2 className="w-5 h-5 text-navy-700" />
          )}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-navy-900">{o.titel}</h3>
            {o.min_compliance_tier && (
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TIER_STIJL[o.min_compliance_tier]}`}>
                <Medal className="w-3 h-3" />
                {t('werkplek.opdrachten.vanafTier', { tier: t(`werkplek.tier.${o.min_compliance_tier}`) })}
              </span>
            )}
            {o.application_status && (
              <span className="rounded-full bg-green-50 border border-green-200 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                {t('werkplek.opdrachten.gereageerd')}
              </span>
            )}
          </div>
          <p className="text-sm text-navy-600">{o.organisatie || t('werkplek.onbekendeOrganisatie')}</p>
          {o.beschrijving && <p className="mt-2 text-sm text-navy-500 line-clamp-2">{o.beschrijving}</p>}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-navy-500">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {regio}
            </span>
            {afstand && <span>{afstand}</span>}
            <span>{typeLabel(o.professional_type)}</span>
            {uren && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {uren}
              </span>
            )}
            {tarief && (
              <span className="inline-flex items-center gap-1">
                <Euro className="w-3.5 h-3.5" />
                {tarief}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

/** URL-waarde → straal: km, null = heel Nederland, undefined = volgt het profiel. */
const leesStraal = (waarde: string | null): number | null | undefined =>
  waarde === null ? undefined : waarde === 'landelijk' ? null : Number(waarde) || undefined;

/**
 * Dashboard › Opdrachten: de landelijke marktplaats, gefilterd op werkgebied.
 * Bovenaan de reisradius-kaart; standaard met de straal uit het profiel. Een
 * andere straal geldt alleen voor deze zoekopdracht (in de URL) en verandert
 * het profiel niet. De server toont alleen opdrachten binnen de eigen
 * discipline(s) en vetting-tier.
 */
const ProfessionalWerkplekOpdrachtenPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const [params, setParams] = useSearchParams();
  const straalParam = params.get('straal');
  const straal = leesStraal(straalParam);
  const page = Math.max(1, Number(params.get('page')) || 1);
  const [zoekInvoer, setZoekInvoer] = useState(params.get('zoek') || '');
  const zoek = params.get('zoek') || '';
  // Live slider-stand; pas na een korte pauze naar de URL (en dus de server).
  const [kaartStraal, setKaartStraal] = useState<number | null | undefined>(straal);

  // Zoeken met een korte vertraging; de URL is de bron van waarheid (deelbaar, terugknop werkt).
  useEffect(() => {
    const id = setTimeout(() => {
      if (zoekInvoer.trim() === zoek) return;
      setParams((p) => {
        const n = new URLSearchParams(p);
        if (zoekInvoer.trim()) n.set('zoek', zoekInvoer.trim());
        else n.delete('zoek');
        n.delete('page');
        return n;
      }, { replace: true });
    }, 350);
    return () => clearTimeout(id);
  }, [zoekInvoer, zoek, setParams]);

  // Terugknop of reset: de slider volgt de URL.
  useEffect(() => {
    setKaartStraal(leesStraal(straalParam));
  }, [straalParam]);

  useEffect(() => {
    if (kaartStraal === undefined) return;
    const doel = kaartStraal === null ? 'landelijk' : String(kaartStraal);
    if (doel === straalParam) return;
    const id = setTimeout(() => {
      setParams((p) => {
        const n = new URLSearchParams(p);
        n.set('straal', doel);
        n.delete('page');
        return n;
      }, { replace: true });
    }, 350);
    return () => clearTimeout(id);
  }, [kaartStraal, straalParam, setParams]);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['professional-werkplek-opdrachten', straalParam ?? 'profiel', zoek, page],
    queryFn: () =>
      werkplekApi.opdrachten({ straal: straal === undefined ? undefined : straal ?? 'landelijk', search: zoek || undefined, page }),
    placeholderData: keepPreviousData,
  });

  const terugNaarProfiel = () =>
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.delete('straal');
      n.delete('page');
      return n;
    });

  const naarPagina = (nieuw: number) =>
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.set('page', String(nieuw));
      return n;
    });

  const profielStraal = data?.profiel_straal_km ?? null;
  const kaartWaarde = kaartStraal !== undefined ? kaartStraal : profielStraal;
  const afwijkend = kaartStraal !== undefined && kaartStraal !== profielStraal;
  const straalTekst = (km: number | null) => (km === null ? t('werkplek.opdrachten.heelNederland') : `${km} km`);

  return (
    <ProfessionalDashboardSchil
      titel={t('werkplek.opdrachten.titel')}
      ondertitel={t('werkplek.opdrachten.ondertitel')}
      acties={
        data?.tier && (
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${TIER_STIJL[data.tier]}`}>
            <Medal className="w-3.5 h-3.5" />
            {t('werkplek.opdrachten.jouwTier', { tier: t(`werkplek.tier.${data.tier}`) })}
          </span>
        )
      }
    >
      <div className="p-4 sm:p-6 space-y-5">
        {/* Werkgebied: alleen voor deze zoekopdracht, het profiel blijft ongewijzigd */}
        {data && !data.blokkade && (
          <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="font-semibold text-navy-900">{t('werkplek.opdrachten.werkgebied')}</h2>
                <p className="text-sm text-navy-500">
                  {afwijkend
                    ? t('werkplek.opdrachten.werkgebiedAfwijkend', { profiel: straalTekst(profielStraal) })
                    : t('werkplek.opdrachten.werkgebiedProfiel')}
                </p>
              </div>
              {afwijkend && (
                <button
                  type="button"
                  onClick={terugNaarProfiel}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-navy-700 hover:bg-navy-50"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t('werkplek.opdrachten.terugNaarProfiel', { profiel: straalTekst(profielStraal) })}
                </button>
              )}
            </div>
            <ReisradiusMap radiusKm={kaartWaarde} onRadiusChange={setKaartStraal} />
            {!data.locatie_bekend && data.straal_km !== null && (
              <p className="mt-2 text-sm text-amber-700">{t('werkplek.opdrachten.alleenRemote')}</p>
            )}
          </section>
        )}

        {/* Opdrachtenbank */}
        <div className="space-y-4 min-w-0">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
            <input
              type="search"
              value={zoekInvoer}
              onChange={(e) => setZoekInvoer(e.target.value)}
              placeholder={t('werkplek.opdrachten.zoek')}
              className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm focus:border-navy-500 focus:ring-1 focus:ring-navy-500"
            />
          </label>

          {data?.blokkade && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3">
              <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-red-800">{t(`werkplek.opdrachten.blokkade.${data.blokkade}.titel`)}</p>
                <p className="text-red-700 mt-0.5">{t(`werkplek.opdrachten.blokkade.${data.blokkade}.tekst`)}</p>
                <Link to="/professional/belagom?view=screening" className="mt-2 inline-block font-semibold text-red-800 underline">
                  {t('werkplek.opdrachten.naarScreening')}
                </Link>
              </div>
            </div>
          )}

          {isError && <p className="text-sm text-red-600">{t('werkplek.laadFout')}</p>}

          {isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-28 rounded-xl border border-gray-200 bg-white animate-pulse" />
              ))}
            </div>
          ) : (
            !data?.blokkade && (
              <>
                <p className="text-sm text-navy-500">
                  {t('werkplek.opdrachten.aantal', { count: data?.pagination.total ?? 0 })}
                  {data?.disciplines?.length ? ` · ${data.disciplines.map(typeLabel).join(', ')}` : ''}
                </p>
                {(data?.opdrachten.length ?? 0) === 0 ? (
                  <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-navy-500">
                    {t('werkplek.opdrachten.leeg')}
                  </div>
                ) : (
                  <div className={`space-y-3 ${isFetching ? 'opacity-70' : ''}`}>
                    {data!.opdrachten.map((o) => (
                      <OpdrachtKaart key={o.id} o={o} />
                    ))}
                  </div>
                )}
                {(data?.pagination.pages ?? 1) > 1 && (
                  <div className="flex items-center justify-center gap-3 pt-2">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => naarPagina(page - 1)}
                      className="p-2 rounded-lg border border-gray-200 bg-white disabled:opacity-40"
                      aria-label={t('werkplek.opdrachten.vorige')}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-navy-600">
                      {page} / {data!.pagination.pages}
                    </span>
                    <button
                      type="button"
                      disabled={page >= data!.pagination.pages}
                      onClick={() => naarPagina(page + 1)}
                      className="p-2 rounded-lg border border-gray-200 bg-white disabled:opacity-40"
                      aria-label={t('werkplek.opdrachten.volgende')}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </>
            )
          )}
        </div>
      </div>
    </ProfessionalDashboardSchil>
  );
};

export default ProfessionalWerkplekOpdrachtenPage;
